import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaImage, FaPlus, FaLink, FaCalendar, FaClock, FaEye, FaFilter } from "react-icons/fa";

import Pagination from "Components/Pagination";
import PopupApplyModal from "Components/PopupApplyModal";
import CommonModal from "Components/CommonModal";
import { getPopupList } from "JS/API";
import { changeState, addZero, catchError } from "JS/common";

// 디자인 시스템 임포트
import PageContainer from "Components/UI/PageContainer";
import Button from "Components/UI/Button";
import Select from "Components/UI/Select";
import StatusIndicator from "Components/UI/StatusIndicator";
import { TableContainer, Table, TableHeader, TableCell } from "Components/UI/Table";
import { commonStyles, designTokens, listPageStyles } from "utils/designSystem";

const PopUp = () => {
  const navigate = useNavigate();
  const [pageInfo, setPageInfo] = useState({
    page: 1,
    totalPage: 1,
    limit: 10,
  });
  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });
  const [info, setInfo] = useState({
    id: "",
    img_url: "",
    link_url: "",
    width: "",
    height: "",
    start_date: "",
    end_date: "",
  });
  const [list, setList] = useState([]);
  const [due, setDue] = useState("all");
  const [mode, setMode] = useState("");
  const [modal, setModal] = useState(false);
  let prevent = false;

  // 추가 스타일
  const pageStyles = {
    controlBar: {
      ...commonStyles.card,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: designTokens.spacing.lg,
      marginBottom: designTokens.spacing.lg,
      flexWrap: 'wrap',
      gap: designTokens.spacing.md
    },
    filterGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.md
    },
    actionGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.md
    },
    previewImage: {
      width: '80px',
      height: '60px',
      objectFit: 'cover',
      borderRadius: designTokens.borderRadius.md,
      border: `1px solid ${designTokens.colors.borderLight}`,
      cursor: 'pointer',
      transition: 'transform 0.2s ease'
    },
    linkText: {
      color: designTokens.colors.primary,
      textDecoration: 'none',
      fontSize: '14px',
      wordBreak: 'break-all',
      lineHeight: '1.4',
      cursor: 'pointer'
    },
    dateInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.xs,
      fontSize: '13px',
      color: designTokens.colors.text.secondary
    },
    noDataContainer: listPageStyles.noDataContainer};

  // 게시 상태별 스타일과 아이콘
  const getStatusConfig = (status) => {
    const configs = {
      '게시 전': {
        variant: 'pending',
        icon: <FaClock size={12} />,
        badgeVariant: 'warning'
      },
      '게시 중': {
        variant: 'valid',
        icon: <FaEye size={12} />,
        badgeVariant: 'success'
      },
      '게시 종료': {
        variant: 'invalid',
        icon: <FaClock size={12} />,
        badgeVariant: 'neutral'
      }
    };
    return configs[status] || configs['게시 전'];
  };

  //= 날짜 정리해서 리턴해 주는 함수
  const returnDate = (int) => {
    const date = new Date(int * 1000);
    return `${date.getFullYear()}-${addZero(date.getMonth() + 1)}-${addZero(
      date.getDate()
    )} ${addZero(date.getHours())}:${addZero(date.getMinutes())}`;
  };

  //= 게시 상태 계산 함수
  const returnStatus = (s, e) => {
    const now = new Date();
    const start = new Date(s);
    const end = new Date(e);
    if (now < start) return "게시 전";
    else if (start < now && now < end) return "게시 중";
    else if (end < now) return "게시 종료";
    else return "";
  };

  //= 팝업 리스트 불러오기
  const getList = async () => {
    if (prevent) return;
    prevent = true;
    setTimeout(() => {
      prevent = false;
    }, 200);
    const result = await getPopupList(pageInfo, due);
    if (typeof result === "object") {
      setList(result?.data?.data);
      setPageInfo((prev) => {
        const clone = { ...prev };
        clone.totalPage = result?.data?.meta?.total_page;
        clone.limit = result?.data?.meta?.limit;
        return clone;
      });
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  //= 팝업 리스트 렌더
  const renderList = () => {
    return list?.map(({ id, img_url, link_url, width, height, start_date, end_date }) => {
      const startDateFormatted = returnDate(start_date);
      const endDateFormatted = returnDate(end_date);
      const status = returnStatus(startDateFormatted, endDateFormatted);
      const statusConfig = getStatusConfig(status);

      const handleRowClick = () => {
        setMode("edit");
        setModal(true);
        setInfo({
          id,
          img_url,
          link_url: link_url ?? "",
          width: width ?? "",
          height: height ?? "",
          start_date,
          end_date,
        });
      };

      return (
        <tr key={id}>
          <TableCell onClick={handleRowClick}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img 
                src={img_url} 
                alt="팝업 미리보기" 
                style={pageStyles.previewImage}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              />
            </div>
          </TableCell>

          <TableCell onClick={handleRowClick}>
            <div style={pageStyles.linkText}>
              <FaLink size={12} style={{ marginRight: designTokens.spacing.xs }} />
              {link_url}
            </div>
          </TableCell>

          <TableCell onClick={handleRowClick}>
            {width && height ? `${width} × ${height}` : "-"}
          </TableCell>

          <TableCell onClick={handleRowClick}>
            <div style={pageStyles.dateInfo}>
              <FaCalendar size={12} />
              {startDateFormatted}
            </div>
          </TableCell>

          <TableCell onClick={handleRowClick}>
            <div style={pageStyles.dateInfo}>
              <FaCalendar size={12} />
              {endDateFormatted}
            </div>
          </TableCell>

          <TableCell onClick={handleRowClick}>
            <StatusIndicator status={statusConfig.variant}>
              {statusConfig.icon}
              {status}
            </StatusIndicator>
          </TableCell>
        </tr>
      );
    });
  };

  useEffect(() => {
    document.title = "마크픽 관리자 > 팝업 관리";
  }, []);

  useEffect(() => {
    if (!modal) {
      setInfo({
        id: "",
        img_url: "",
        link_url: "",
        width: "",
        height: "",
        start_date: "",
        end_date: "",
      });
      getList();
    }
  }, [due, pageInfo.page, pageInfo.limit, modal]);

  return (
    <PageContainer 
      title="POP-UP" 
      icon={<FaImage />}
    >
      {/* 컨트롤 바 */}
      <div style={pageStyles.controlBar}>
        <div style={pageStyles.filterGroup}>
          <FaFilter size={16} style={{ color: designTokens.colors.text.tertiary }} />
          
          <Select
            value={pageInfo.limit}
            onChange={(e) =>
              changeState(setPageInfo, "limit", e.target.value)
            }
            style={{ minWidth: '140px' }}
          >
            <option value={10}>10개씩 보기</option>
            <option value={30}>30개씩 보기</option>
            <option value={50}>50개씩 보기</option>
          </Select>

          <Select
            value={due}
            onChange={(e) => {
              setDue(e.target.value);
              setPageInfo((prev) => ({ ...prev, page: 1 }));
            }}
            style={{ minWidth: '120px' }}
          >
            <option value="all">전체 보기</option>
            <option value="waiting">게시 전</option>
            <option value="posting">게시 중</option>
            <option value="expired">게시 종료</option>
          </Select>
        </div>

        <div style={pageStyles.actionGroup}>
          <Button
            variant="success"
            icon={<FaPlus />}
            onClick={() => {
              setMode("apply");
              setModal(true);
            }}
          >
            팝업 추가
          </Button>
        </div>
      </div>

      {/* 테이블 또는 빈 상태 */}
      {list?.length ? (
        <TableContainer>
          <Table>
            <colgroup>
              <col width="18%" />
              <col width="22%" />
              <col width="12%" />
              <col width="16%" />
              <col width="16%" />
              <col width="16%" />
            </colgroup>
            <thead>
              <tr>
                <TableHeader>미리보기</TableHeader>
                <TableHeader>링크</TableHeader>
                <TableHeader>크기</TableHeader>
                <TableHeader>게시 날짜</TableHeader>
                <TableHeader>게시 만료일</TableHeader>
                <TableHeader>게시 상태</TableHeader>
              </tr>
            </thead>
            <tbody>{renderList()}</tbody>
          </Table>
        </TableContainer>
      ) : (
        <div style={pageStyles.noDataContainer}>
          <FaImage 
            size={48} 
            style={{ 
              color: designTokens.colors.text.muted, 
              marginBottom: designTokens.spacing.md 
            }} 
          />
          <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: designTokens.spacing.xs }}>
            등록된 팝업이 없습니다
          </div>
          <div style={{ fontSize: '14px' }}>
            새로운 팝업을 추가해보세요.
          </div>
        </div>
      )}

      <Pagination pageInfo={pageInfo} setPageInfo={setPageInfo} />

      {/* 모달들 */}
      {modal && (
        <PopupApplyModal
          setModal={setModal}
          mode={mode}
          info={info}
          setInfo={setInfo}
        />
      )}
      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "logout") navigate("/");
            else return;
          }}
        />
      )}
    </PageContainer>
  );
};

export default PopUp;